import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  TextField,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon
} from '@mui/icons-material';
import adkWorkflowService from '../../services/adkWorkflow.service';

const ApprovalDialog = ({
  open,
  onClose,
  approvalRequestId,
  plan,
  onApproved,
  onRejected
}) => {
  const [selectedChanges, setSelectedChanges] = useState([]);
  const [reviewNotes, setReviewNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);

  // Initialize selected changes when plan is loaded
  useEffect(() => {
    if (plan && plan.affectedSections) {
      // Select all changes by default
      const allChanges = [
        {
          sectionId: plan.targetSection?.sectionId,
          sectionNumber: plan.targetSection?.sectionNumber,
          title: plan.targetSection?.title,
          change: plan.targetSection?.change,
          reason: plan.targetSection?.reason
        },
        ...(plan.affectedSections || [])
      ].filter(change => change.sectionId); // Filter out null/undefined

      setSelectedChanges(allChanges.map((_, index) => index));
    }
  }, [plan]);

  // Fetch status if approvalRequestId is provided
  useEffect(() => {
    if (open && approvalRequestId && !plan) {
      fetchStatus();
    }
  }, [open, approvalRequestId, plan]);

  const fetchStatus = async () => {
    try {
      const result = await adkWorkflowService.getApprovalRequestStatus(approvalRequestId);
      setStatus(result);
    } catch (err) {
      console.error('Error fetching status:', err);
      setError(err.message || 'Failed to fetch approval request status');
    }
  };

  const handleToggleChange = (index) => {
    setSelectedChanges(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const handleApprove = () => {
    if (selectedChanges.length === 0) {
      setError('Please select at least one change to approve.');
      return;
    }
    submitApproval();
  };

  const submitApproval = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Get the actual change objects for selected indices
      const allChanges = [
        {
          sectionId: plan.targetSection?.sectionId,
          sectionNumber: plan.targetSection?.sectionNumber,
          title: plan.targetSection?.title,
          change: plan.targetSection?.change,
          reason: plan.targetSection?.reason,
          projectId: plan.projectId
        },
        ...(plan.affectedSections || []).map(s => ({
          ...s,
          projectId: plan.projectId
        }))
      ].filter(change => change.sectionId);

      const approvedChanges = selectedChanges.map(index => allChanges[index]);

      const result = await adkWorkflowService.approveModificationPlan({
        approvalRequestId,
        approvedChanges,
        reviewNotes: reviewNotes.trim() || null,
        meaning: 'approved'
      });

      if (onApproved) {
        onApproved(result);
      }

      // Reset and close
      setSelectedChanges([]);
      setReviewNotes('');
      onClose();
    } catch (err) {
      console.error('Error approving plan:', err);
      setError(err.message || 'Failed to approve modification plan. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = () => {
    if (onRejected) {
      onRejected(approvalRequestId);
    }
    setSelectedChanges([]);
    setReviewNotes('');
    onClose();
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSelectedChanges([]);
      setReviewNotes('');
      setError(null);
      onClose();
    }
  };

  // Get risk color
  const getRiskColor = (riskLevel) => {
    switch (riskLevel?.toLowerCase()) {
      case 'high': return '#d32f2f';
      case 'medium': return '#ed6c02';
      case 'low': return '#2e7d32';
      default: return '#757575';
    }
  };

  // Display status if available
  if (status && !plan) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          pb: 1.5
        }}>
          <Typography variant="h6">Approval Request Status</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity={status.status === 'completed' ? 'success' : status.status === 'failed' ? 'error' : 'info'}>
            Status: <strong>{status.status}</strong>
          </Alert>
          {status.results && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Execution Results:</Typography>
              <List>
                {status.results.map((result, idx) => (
                  <ListItem key={idx}>
                    <ListItemIcon>
                      {result.success ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={`Section ${result.sectionNumber || result.sectionId}`}
                      secondary={result.message}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (!plan) {
    return null;
  }

  const allChanges = [
    {
      sectionId: plan.targetSection?.sectionId,
      sectionNumber: plan.targetSection?.sectionNumber,
      title: plan.targetSection?.title,
      change: plan.targetSection?.change,
      reason: plan.targetSection?.reason
    },
    ...(plan.affectedSections || [])
  ].filter(change => change.sectionId);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        pb: 1.5
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon sx={{ color: '#1976d2' }} />
          <Typography variant="h6" component="div">
            Review Modification Plan
          </Typography>
        </Box>
        <IconButton 
          onClick={handleClose} 
          size="small"
          disabled={isSubmitting}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Plan Summary */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Plan Summary</Typography>
            <Chip 
              label={`Risk: ${plan.riskLevel?.toUpperCase() || 'UNKNOWN'}`}
              sx={{ 
                bgcolor: getRiskColor(plan.riskLevel),
                color: 'white',
                fontWeight: 'bold'
              }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Estimated Changes:</strong> {plan.estimatedChanges || allChanges.length} section(s)
          </Typography>
          {plan.warnings && plan.warnings.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="warning.main" gutterBottom>
                <WarningIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                Warnings:
              </Typography>
              <List dense>
                {plan.warnings.map((warning, idx) => (
                  <ListItem key={idx} sx={{ py: 0.5 }}>
                    <ListItemText 
                      primary={warning}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>

        {/* Target Section */}
        {plan.targetSection && (
          <Accordion defaultExpanded sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedChanges.includes(0)}
                      onChange={() => handleToggleChange(0)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  }
                  label={
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      Target Section: {plan.targetSection.sectionNumber} - {plan.targetSection.title}
                    </Typography>
                  }
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ pl: 4 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Change:</strong> {plan.targetSection.change}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Reason:</strong> {plan.targetSection.reason}
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Affected Sections */}
        {plan.affectedSections && plan.affectedSections.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon color="primary" />
              Affected Sections ({plan.affectedSections.length})
            </Typography>
            {plan.affectedSections.map((section, index) => {
              const changeIndex = index + 1; // +1 because index 0 is target section
              return (
                <Accordion key={index} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedChanges.includes(changeIndex)}
                            onChange={() => handleToggleChange(changeIndex)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        }
                        label={
                          <Typography variant="subtitle2">
                            {section.sectionNumber} - {section.title}
                          </Typography>
                        }
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ pl: 4 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Change:</strong> {section.change}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Reason:</strong> {section.reason}
                      </Typography>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Review Notes */}
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Review Notes (Optional)"
          placeholder="Add any notes or comments about this modification plan..."
          value={reviewNotes}
          onChange={(e) => setReviewNotes(e.target.value)}
          disabled={isSubmitting}
          sx={{ mt: 2 }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, borderTop: '1px solid rgba(0, 0, 0, 0.12)', pt: 2 }}>
        <Button 
          onClick={handleReject}
          color="error"
          disabled={isSubmitting}
          startIcon={<CancelIcon />}
        >
          Reject
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button 
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          onClick={handleApprove}
          variant="contained"
          color="primary"
          startIcon={isSubmitting ? <CircularProgress size={16} /> : <CheckCircleIcon />}
          disabled={isSubmitting || selectedChanges.length === 0}
          sx={{ minWidth: 140 }}
        >
          {isSubmitting ? 'Executing...' : `Approve (${selectedChanges.length})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApprovalDialog;

